import default150 from "../../../../product/modules/commons/images/product/default-150.png";
import React, {useMemo} from "react";
import {CompletedOrder} from "../../../infrastructure/slices/checkoutCompletedSliceModel";
import {useTranslation} from "react-i18next";
import {getAdjustmentAmount, groupAdjustments, isVatApplicable} from "../../../../shared/utils/adjustments";

const OrderOverview = (props: { order: CompletedOrder }) => {
    const {order} = props;
    const {t} = useTranslation('common');
    const orderAdjustmentGroups = groupAdjustments(order?.adjustments);
    const orderTotalAdjustments = groupAdjustments(order?.order_total?.adjustments);
    const relationAttributes = (order?.relation?.data as { attributes?: { vat?: string|null, address?: { country_code?: string|null } } }|null)?.attributes;
    const orderData = order?.data as { cb_relations_webshop_fuel_surcharge_country_code?: string|null }|null;
    const vatApplicable = isVatApplicable(
        relationAttributes?.vat,
        orderData?.cb_relations_webshop_fuel_surcharge_country_code ?? relationAttributes?.address?.country_code,
    );
    const validOrderItems = (order?.order_items ?? []).filter((item) =>
        item
        && item.type !== 'missing'
        && item.total_price?.number !== undefined
        && item.unit_price?.formatted !== undefined
        && (item.purchased_entity?.title || item.title)
    );

    const headers = useMemo(() => [
        {
            text: '',
            className: 'cart-th-image',
        },
        {
            text: t('Description'),
            className: 'cart-th-desc',
        },
        {
            text: t('Qty'),
            className: 'cart-th-qty text-center',
        },
        {
            text: t('Price'),
            className: 'cart-th-price text-end',
        },
        {
            text: t('Total'),
            className: 'cart-th-total text-end',
        },
    ], [t]);

    const formatNum = (number: string) => {
        return new Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'}).format(
            parseFloat(number),
        );
    }

    const shippingCosts = orderAdjustmentGroups.shippingRows[0] ?? null;
    const fuelCosts = orderAdjustmentGroups.fuelRows[0] ?? null;
    const cashInvoiceCosts = orderAdjustmentGroups.cashInvoiceRows;
    const vatCosts = orderTotalAdjustments.taxRows;

    const amts = validOrderItems.map(item => item.total_price.number ?? '0');

    const getVatTotal = (pct: string) => {
        return (
            validOrderItems.length > 0 ?
                validOrderItems
                    .map(item => item.adjustments.find(x => x.type === 'tax' && x.percentage === pct))
                    .map(x => x ?
                        parseFloat(x.amount.replace(',', '.').split(' ')[0])
                        : 0
                    )
                : []
        ).reduce((total, amt) => total + amt, 0);
    }

    const subtotal = amts.reduce((partialSum, a) => partialSum + parseFloat(a as string), 0);


    if (validOrderItems.length < 1) {
        return  <></>
    }

    const counter = validOrderItems.map(item => parseInt(item.quantity)).reduce((partialSum, a) => partialSum + a, 0);

    const formatPct = (num: string) => `${parseFloat(num) * 100}%`

    return <div className={'checkout-completed-overview cart'}>
        <div className="table-responsive">
            <table className={'w-100'}>
                <thead>
                <tr className={'cart-table-header'}>
                    {headers.map((header, delta) => <th key={delta} className={header.className}>
                        {header.text}
                    </th>)}
                </tr>
                </thead>
                <tbody>
                {validOrderItems.map((item, delta) => {
                    const title = item.purchased_entity?.title || item.title;
                    const sku = item.purchased_entity?.sku || item.product_sku;

                    return <tr key={item.id ?? delta}>
                        <td>
                            {item?.product_images === undefined ? (
                                <img loading="lazy" decoding="async"
                                    src={default150}
                                    alt={title}
                                    width={'100'}/>
                            ) : (
                                <img loading="lazy" decoding="async"
                                    src={item.product_images && (item?.product_images?.length ?? 0) > 0 ? item.product_images[0].image_style_uri.thumbnail ?? default150 : default150}
                                    alt={title}
                                    width={'100'}/>
                            )}
                        </td>
                        <td className={'d-flex flex-column justify-content-between'}>
                            <div>
                                <h6>{title}</h6>
                                {sku && <span>{t('Article number')} {sku}</span>}
                            </div>
                        </td>
                        <td className={'text-center qty'}>
                            {parseInt(item.quantity)}
                        </td>
                        <td className={'text-end cart-price'}>
                            {item.unit_price.formatted}
                        </td>
                        <td className={'text-end'}>
                            {item.total_price.formatted}
                        </td>
                    </tr>
                })}
                </tbody>
                <tfoot>
                <tr>
                    <td colSpan={3}></td>
                    <td>{t('Articles')} ({counter})</td>
                    <td className={'text-end'}>{formatNum(`${subtotal}`)}</td>
                </tr>
                {vatCosts.length > 0 && vatCosts.map((item, index) => <tr key={index}>
                    <td colSpan={3}></td>
                    <td>{item.label} ({formatPct(item.percentage ?? '0')})</td>
                    <td className={'text-end'}>
                        {formatNum(`${getVatTotal(item.percentage ?? '0')}`)}
                    </td>
                </tr>)}
                {shippingCosts && <tr>
                    <td colSpan={3}></td>
                    <td>{t(vatApplicable ? 'Transport costs (incl. VAT)' : 'Transport costs')}</td>
                    <td className={'text-end'}>
                        {formatNum(getAdjustmentAmount(shippingCosts.amount).replace(',', '.').split(' ')[0])}
                    </td>
                </tr>}
                {fuelCosts && <tr>
                    <td colSpan={3}></td>
                    <td>{t(vatApplicable ? 'Fuel costs (incl. VAT)' : 'Fuel costs')}</td>
                    <td className={'text-end'}>
                        {formatNum(getAdjustmentAmount(fuelCosts.amount).replace(',', '.').split(' ')[0])}
                    </td>
                </tr>}
                {cashInvoiceCosts.length > 0 && cashInvoiceCosts.map((item, index) => <tr key={`cash-invoice-${index}`}>
                    <td colSpan={3}></td>
                    <td>{item.label}</td>
                    <td className={'text-end'}>
                        {formatNum(getAdjustmentAmount(item.amount).replace(',', '.').split(' ')[0])}
                    </td>
                </tr>)}
                <tr className={'total'}>
                    <td colSpan={3}></td>
                    <td>{t(vatApplicable ? 'Total incl. VAT' : 'Total')}</td>
                    <td className={'text-end'}>{order.total_price.formatted}</td>
                </tr>
                </tfoot>
            </table>
        </div>
    </div>
}

export default OrderOverview;
